import type { Call, CallAction, CommandReceipt } from '@bcio-voice/contracts';
import type { ApiResult, CallControlApi } from './api.js';
import { newIdempotencyKey, type UuidFactory } from './ids.js';
import { delay, TimerSet, type Scheduler } from './scheduler.js';

/** Contract action names. (TypeBox infers `CallAction['action']` as plain `string`, so the union is spelled out.) */
export type ActionName = 'answer' | 'reject' | 'hangup' | 'hold' | 'resume' | 'dtmf';
export type CommandKind = ActionName | 'create';
/** A `CallAction` the engine can construct: always one of the contract's enumerated actions. */
export type CallActionRequest =
  | { readonly action: Exclude<ActionName, 'dtmf'>; readonly expected_version: number }
  | { readonly action: 'dtmf'; readonly expected_version: number; readonly digits: string };

/**
 * `accepted`/`dispatching`/`applied`/`failed`/`unknown` are server receipt states. The others are client states:
 * `sending` (no response yet), `unconfirmed` (no HTTP response after retries; same key may be re-sent),
 * `conflict` (409, the call was refetched; nothing is re-sent automatically) and `rejected` (other 4xx/5xx).
 * None of these means a phone rang or a call connected; that comes only from call state.
 */
export type CommandPhase = 'sending' | CommandReceipt['status'] | 'unconfirmed' | 'conflict' | 'rejected';

export interface CommandView {
  readonly kind: CommandKind;
  readonly phase: CommandPhase;
  readonly command_id: string | null;
  readonly idempotency_key: string;
  readonly expected_version: number | null;
  readonly digits: string | null;
  readonly error: { readonly code: string; readonly message: string } | null;
  readonly started_at: number;
  readonly updated_at: number;
}

export interface CommandHooks {
  /** A receipt (or command poll) carried a newer authoritative call snapshot. */
  receipt(callId: string, call: Call): void;
  /** A 409 means the local version is stale: re-fetch the call. */
  refresh(callId: string): void;
  update(callId: string, view: CommandView): void;
}

export interface CommandTiming {
  /** Same-key retries after a network failure (no HTTP response). */
  readonly networkRetryDelaysMs: readonly number[];
  /** UX 7.4: 1, 2 and 4 s, then every 10 s. */
  readonly pollDelaysMs: readonly number[];
  readonly pollMaxMs: number;
}

export const DEFAULT_COMMAND_TIMING: CommandTiming = Object.freeze({
  networkRetryDelaysMs: Object.freeze([500, 1500, 3000]),
  pollDelaysMs: Object.freeze([1000, 2000, 4000, 10000]),
  pollMaxMs: 120_000,
});

const settledStatus = (phase: CommandPhase) => phase === 'applied' || phase === 'failed';

export class CommandRunner {
  private readonly dedupe = new Map<string, Promise<CommandView>>();
  private readonly timers: TimerSet;
  private disposed = false;

  constructor(
    private readonly api: CallControlApi,
    private readonly scheduler: Scheduler,
    private readonly uuid: UuidFactory,
    private readonly hooks: CommandHooks,
    private readonly timing: CommandTiming = DEFAULT_COMMAND_TIMING,
  ) {
    this.timers = new TimerSet(scheduler);
  }

  /**
   * Sends one call action with a fresh idempotency key. A second request with the same `dedupeKey`
   * while the first is unresolved returns the first promise (duplicate activation guard).
   * Resolves at the first definitive HTTP outcome; receipt polling continues in the background.
   */
  run(callId: string, action: CallActionRequest, dedupeKey: string | null): Promise<CommandView> {
    if (dedupeKey) {
      const existing = this.dedupe.get(dedupeKey);
      if (existing) return existing;
    }
    const promise = this.execute(callId, action);
    if (dedupeKey) {
      this.dedupe.set(dedupeKey, promise);
      void promise.finally(() => { if (this.dedupe.get(dedupeKey) === promise) this.dedupe.delete(dedupeKey); });
    }
    return promise;
  }

  /** Sends `POST /calls` (or any keyed request) with retries that reuse the same key. */
  async send<T>(key: string, kind: CommandKind, callIdForHooks: string | null, request: () => Promise<ApiResult<T>>, onUpdate: (view: CommandView) => void): Promise<{ view: CommandView; value: T | null }> {
    const started = this.scheduler.now();
    let view: CommandView = freezeView({ kind, phase: 'sending', command_id: null, idempotency_key: key, expected_version: null, digits: null, error: null, started_at: started, updated_at: started });
    onUpdate(view);
    for (let attempt = 0; ; attempt++) {
      const result = await request();
      if (this.disposed) return { view, value: null };
      if (result.ok) return { view, value: result.value };
      const retryable = result.kind === 'network' || (result.kind === 'http' && result.status >= 500 && result.retryable);
      if (retryable && attempt < this.timing.networkRetryDelaysMs.length) {
        await delay(this.scheduler, this.timing.networkRetryDelaysMs[attempt]!);
        if (this.disposed) return { view, value: null };
        continue;
      }
      const error = result.kind === 'http' ? { code: result.code, message: result.message } : { code: result.kind === 'network' ? 'network_error' : 'invalid_response', message: result.message };
      const phase: CommandPhase = result.kind === 'network' || result.kind === 'invalid_response' ? 'unconfirmed' : result.status === 409 ? 'conflict' : 'rejected';
      if (phase === 'conflict' && callIdForHooks) this.hooks.refresh(callIdForHooks);
      view = freezeView({ ...view, phase, error, updated_at: this.scheduler.now() });
      onUpdate(view);
      return { view, value: null };
    }
  }

  /** Polls `GET /commands/{id}` until applied/failed or the bounded window ends. */
  track(callId: string, base: CommandView, receipt: CommandReceipt): CommandView {
    this.hooks.receipt(callId, receipt.call);
    const view = freezeView({ ...base, phase: receipt.status, command_id: receipt.command_id, updated_at: this.scheduler.now() });
    this.hooks.update(callId, view);
    if (!settledStatus(view.phase)) this.schedulePoll(callId, view, 0);
    return view;
  }

  cancel(callId: string): void {
    for (const name of [...this.timerNames]) if (name.startsWith(`${callId}:`)) { this.timers.clear(name); this.timerNames.delete(name); }
  }

  dispose(): void {
    this.disposed = true;
    this.timers.clearAll();
    this.timerNames.clear();
    this.dedupe.clear();
  }

  get pendingPolls(): number { return this.timers.size; }

  private readonly timerNames = new Set<string>();

  private async execute(callId: string, action: CallActionRequest): Promise<CommandView> {
    const key = newIdempotencyKey('act', this.uuid);
    const digits = action.action === 'dtmf' ? action.digits : null;
    const body: CallAction = action;
    let base: CommandView | null = null;
    const { view, value } = await this.send(key, action.action, callId, () => this.api.callAction(callId, body, key), v => {
      base = freezeView({ ...v, expected_version: action.expected_version, digits });
      this.hooks.update(callId, base);
    });
    const latest = freezeView({ ...(base ?? view), expected_version: action.expected_version, digits });
    if (!value) return latest;
    return this.track(callId, latest, value);
  }

  private schedulePoll(callId: string, view: CommandView, attempt: number): void {
    if (this.disposed || !view.command_id) return;
    const elapsed = this.scheduler.now() - view.started_at;
    if (elapsed >= this.timing.pollMaxMs) return;
    const delays = this.timing.pollDelaysMs;
    const wait = delays[Math.min(attempt, delays.length - 1)] ?? 10_000;
    const name = `${callId}:${view.command_id}`;
    this.timerNames.add(name);
    this.timers.set(name, wait, () => {
      this.timerNames.delete(name);
      void this.api.getCommand(view.command_id!).then(result => {
        if (this.disposed) return;
        if (!result.ok) { this.schedulePoll(callId, view, attempt + 1); return; }
        this.hooks.receipt(callId, result.value.call);
        const next = freezeView({ ...view, phase: result.value.status, updated_at: this.scheduler.now() });
        this.hooks.update(callId, next);
        if (!settledStatus(next.phase)) this.schedulePoll(callId, next, attempt + 1);
      });
    });
  }
}

function freezeView(view: CommandView): CommandView { return Object.freeze({ ...view }); }
